import React, { useState } from 'react';

const EmergencyControls = ({ settings, onUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const handleEmergencyStop = async () => {
    setUpdating(true);
    setStatusMessage('Activating emergency stop...');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await fetch('/api/settings/emergency-stop', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Improved error handling
        let errorMessage = 'Failed to activate emergency stop';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response as JSON, use the status text
          errorMessage = `Failed: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Avoid parsing if the response is empty
      if (response.headers.get('content-length') !== '0') {
        await response.json();
      }

      setStatusMessage('Emergency stop activated');
      
      // Notify parent component to refresh data
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (error) {
      console.error('Error activating emergency stop:', error);
      // Use a simple string error message instead of object
      const errorMessage = error.message || 'Unknown error';
      setStatusMessage(`Failed: ${errorMessage}`);
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setUpdating(false);
    }
  };

  const handleEmergencyReset = async () => {
    setUpdating(true);
    setStatusMessage('Resetting emergency stop...');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
  
      // Make sure we include all required fields
      const requestBody = {
        ...settings,  // Include existing settings
        emergency_mode: false,
        is_enabled: true,
        // Explicitly include the required fields with default values if missing
        max_positions: settings.max_positions || 20,  // Default to 20 if missing
        risk_percentage: settings.risk_percentage || 1.0  // Default to 1.0 if missing
      };
      
      console.log('Sending reset request with data:', requestBody);
  
      // Update the emergency settings
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
  
      if (!response.ok) {
        // Handle validation errors specifically
        if (response.status === 422) {
          const errorData = await response.json();
          console.error('Validation error details:', errorData);
          
          // Extract useful information from the validation error
          let errorDetail = 'Invalid data format';
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // FastAPI often returns validation errors in this format
              errorDetail = errorData.detail.map(err => 
                `${err.loc.join('.')}: ${err.msg}`
              ).join(', ');
            } else {
              errorDetail = errorData.detail;
            }
          }
          throw new Error(`Validation Error: ${errorDetail}`);
        } else {
          // Handle other error types
          let errorMessage = `Failed to reset emergency stop (${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch (parseError) {
            errorMessage = `Failed: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }
      }
      
      setStatusMessage('Trading enabled');
      
      // Notify parent component to refresh data
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (error) {
      console.error('Error resetting emergency stop:', error);
      const errorMessage = error.message || 'Unknown error';
      setStatusMessage(`Failed: ${errorMessage}`);
      setTimeout(() => setStatusMessage(''), 8000);
    } finally {
      setUpdating(false);
    }
  };
  
  const toggleTrading = async () => {
    setUpdating(true);
    const newStatus = !settings.is_enabled;
    setStatusMessage(`${newStatus ? 'Enabling' : 'Disabling'} trading...`);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
  
      // Make sure we include all required fields
      const requestBody = {
        ...settings,  // Include existing settings
        is_enabled: newStatus,
        // Explicitly include the required fields with default values if missing
        max_positions: settings.max_positions || 20,
        risk_percentage: settings.risk_percentage || 1.0
      };
      
      console.log('Sending toggle request with data:', requestBody);
  
      // Update the trading status
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
  
      if (!response.ok) {
        // Handle validation errors specifically
        if (response.status === 422) {
          const errorData = await response.json();
          console.error('Validation error details:', errorData);
          
          // Extract useful information from the validation error
          let errorDetail = 'Invalid data format';
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              errorDetail = errorData.detail.map(err => 
                `${err.loc.join('.')}: ${err.msg}`
              ).join(', ');
            } else {
              errorDetail = errorData.detail;
            }
          }
          throw new Error(`Validation Error: ${errorDetail}`);
        } else {
          // Handle other error types
          let errorMessage = `Failed to update trading status (${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch (parseError) {
            errorMessage = `Failed: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }
      }
      
      setStatusMessage(`Trading ${newStatus ? 'enabled' : 'disabled'}`);
      
      // Notify parent component to refresh data
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling trading status:', error);
      const errorMessage = error.message || 'Unknown error';
      setStatusMessage(`Failed: ${errorMessage}`);
      setTimeout(() => setStatusMessage(''), 8000);
    } finally {
      setUpdating(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-4">Emergency Controls</h3>
      
      {statusMessage && (
        <div className={`p-3 mb-3 rounded-md text-sm ${
          statusMessage.includes('Failed') 
            ? 'bg-red-100 text-red-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {statusMessage}
        </div>
      )}
      
      <div className="flex space-x-3">
        <button
          onClick={settings.emergency_mode ? handleEmergencyReset : handleEmergencyStop}
          disabled={updating}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            updating 
              ? 'opacity-50 cursor-not-allowed' 
              : settings.emergency_mode 
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
        >
          {updating 
            ? 'Processing...' 
            : settings.emergency_mode 
              ? 'Reset Emergency Stop' 
              : 'Emergency Stop'
          }
        </button>
        
        <button
          onClick={toggleTrading}
          disabled={updating || settings.emergency_mode}
          className={`px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 ${
            settings.is_enabled 
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          Trading {settings.is_enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>
    </div>
  );
};

export default EmergencyControls;